import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Camera,
  ClipboardList,
  MapPin,
  Megaphone,
  MessageCircle,
  ShoppingBag,
  User,
  UserCheck,
} from "lucide-react";
import { PageShell, PageHero } from "@/components/site/PageShell";
import { PostularForm } from "@/components/site/PostularForm";
import { FAQAccordion, faqJsonLd, type FAQItem } from "@/components/site/FAQAccordion";
import { SITE_URL } from "@/lib/site";
import { seoMeta, canonicalLink, absoluteUrl, robotsMeta } from "@/lib/seo";

// Intención de EMPLEO (quien busca trabajo), no comercial: el título y el H1
// llevan siempre "trabajo"/"postula" para no competir con /anfitrionas e
// /impulsadoras-promotoras, que atienden a la empresa que contrata. Alcance
// nacional: se postula desde cualquier ciudad del Perú.
const DEFAULT_TITLE = "Trabajo de Anfitriona, Promotora e Impulsadora en Perú";
const DEFAULT_DESCRIPTION =
  "Postula gratis para trabajar como anfitriona, promotora, impulsadora o modelo en eventos y activaciones de marcas en Lima y regiones del Perú. Part time.";

// Ciudades desde las que se puede postular. No es una promesa de campañas en
// cada una: el formulario guarda ciudad y distrito, y se convoca según dónde
// se activa cada marca.
const CIUDADES = [
  "Lima y Callao",
  "Arequipa",
  "Trujillo",
  "Chiclayo",
  "Piura",
  "Cusco",
  "Ica",
  "Huancayo",
  "Chimbote",
  "Cajamarca",
  "Tacna",
  "Iquitos",
];

const PERFILES = [
  {
    icon: User,
    title: "Anfitrionas de evento",
    desc: "Recepción, registro y atención de invitados en eventos corporativos, ferias y lanzamientos.",
  },
  {
    icon: Megaphone,
    title: "Promotoras y promotores BTL",
    desc: "Activaciones de marca en calle, centros comerciales y eventos: mecánicas, muestras y captación.",
  },
  {
    icon: ShoppingBag,
    title: "Impulsadoras e impulsadores",
    desc: "Impulso de producto en supermercados, farmacias y retail, con capacitación previa de marca.",
  },
  {
    icon: Camera,
    title: "Modelos",
    desc: "Imagen para marcas, lanzamientos y eventos donde la presencia tiene rol protagónico.",
  },
  {
    icon: UserCheck,
    title: "Animadores",
    desc: "Conducción de mecánicas, juegos y escenario en activaciones y eventos masivos.",
  },
];

const faqs: FAQItem[] = [
  {
    q: "¿Es un trabajo fijo o por campañas?",
    a: "Por campañas y jornadas: te convocamos para eventos y activaciones puntuales, que pueden durar desde un día hasta varias semanas. Por eso es ideal si buscas trabajo part time, de fines de semana o compatible con estudios u otro trabajo.",
  },
  {
    q: "¿Necesito experiencia para postular?",
    a: "No siempre. Para eventos corporativos y protocolo se suele pedir experiencia; para ferias, activaciones y campañas masivas hay espacio para perfiles nuevos: antes de cada campaña damos briefing y capacitación. Lo que sí pedimos siempre es puntualidad, buena presentación y trato con el público.",
  },
  {
    q: "¿Cuánto se paga por jornada?",
    a: "Depende de cada campaña y de la ciudad: el pago varía según la duración del turno, el tipo de evento, el perfil requerido y los idiomas. El monto y la forma de pago se acuerdan contigo antes de confirmar cada campaña, sin sorpresas.",
  },
  {
    q: "¿Postular tiene algún costo?",
    a: "No. Postular es gratis y nunca te pediremos pagos, inscripciones ni 'garantías' para acceder a campañas. Desconfía de cualquier supuesta agencia que cobre por darte trabajo.",
  },
  {
    q: "¿Puedo postular si vivo fuera de Lima?",
    a: "Sí. Recibimos postulaciones de todo el Perú. Indica tu ciudad y tu distrito en el formulario: cuando una marca hace una campaña o un evento en tu ciudad, convocamos primero al personal que vive ahí. La mayor parte de las campañas son en Lima Metropolitana y Callao; en regiones se concentran en campañas nacionales y temporadas altas.",
  },
  {
    q: "¿Cómo y cuándo me contactan?",
    a: "Cuando surge una campaña que calza con tu perfil, te escribimos por WhatsApp o te llamamos para confirmarte fechas, funciones y pago. Mantén tu número y tus fotos actualizados para recibir más convocatorias.",
  },
];

export const Route = createFileRoute("/postular")({
  component: Postular,
  head: ({ match }) => {
    const override = match.context.seoOverride;
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/postular",
          image: override?.ogImage ?? undefined,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: canonicalLink(override?.canonicalPath ?? "/postular"),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE_URL}/` },
              {
                "@type": "ListItem",
                position: 2,
                name: "Trabaja con nosotros",
                item: `${SITE_URL}/postular`,
              },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            // WebPage + Occupation, no JobPosting: Google no admite JobPosting
            // para postulaciones generales sin una vacante concreta.
            "@type": "WebPage",
            name: "Trabaja con nosotros · Improve Yourself",
            description: DEFAULT_DESCRIPTION,
            url: absoluteUrl("/postular"),
            inLanguage: "es-PE",
            about: [
              "Anfitriona de eventos",
              "Promotora BTL",
              "Impulsadora de ventas",
              "Modelo para eventos",
              "Animador de eventos",
            ].map((name) => ({
              "@type": "Occupation",
              name,
              occupationLocation: { "@type": "Country", name: "Perú" },
            })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(faqJsonLd(faqs)) },
      ],
    };
  },
});

function Postular() {
  return (
    <PageShell>
      <PageHero
        eyebrow="· Trabaja con nosotros · Todo el Perú"
        h1="Trabajo de anfitriona, promotora e impulsadora en todo el Perú"
        intro="Súmate a nuestra base de staff para eventos y activaciones de marcas en Lima y regiones. Trabajamos por campañas y jornadas — ideal si buscas trabajo part time, de fines de semana o compatible con estudios. Completa tus datos, tu ciudad y tus fotos en 5 minutos; te contactamos por WhatsApp cuando haya una campaña que calce con tu perfil. Postular es gratis."
      />

      <section className="pb-16">
        <div className="mx-auto max-w-2xl px-5 md:px-8">
          <PostularForm />
        </div>
      </section>

      {/* PERFILES — contenido indexable que responde "qué trabajo hay" */}
      <section className="bg-surface py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              · Perfiles
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-secondary md:text-4xl">
              Qué perfiles buscamos
            </h2>
            <p className="mt-4 text-secondary/75">
              Convocamos personal para campañas de marcas en Lima y regiones durante todo el año.
              Puedes postular con más de una habilidad: mientras más completo tu perfil, más
              convocatorias recibes.
            </p>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {PERFILES.map((p) => (
              <article
                key={p.title}
                className="rounded-lg border border-border bg-background p-6 shadow-soft"
              >
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
                  <p.icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <h3 className="mt-4 font-display text-lg font-bold text-secondary">{p.title}</h3>
                <p className="mt-2 text-sm text-secondary/70">{p.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              · Proceso
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-secondary md:text-4xl">
              Cómo funciona
            </h2>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              {
                icon: ClipboardList,
                step: "1",
                title: "Postula en 5 minutos",
                desc: "Completa tus datos, tallas y fotos. No necesitas book profesional: fotos claras y actuales son suficientes.",
              },
              {
                icon: UserCheck,
                step: "2",
                title: "Entras a nuestra base de staff",
                desc: "Revisamos tu perfil y tus habilidades para considerarte en las campañas que calcen contigo.",
              },
              {
                icon: MessageCircle,
                step: "3",
                title: "Te contactamos por WhatsApp",
                desc: "Cuando hay una campaña para tu perfil te confirmamos fechas, funciones y pago antes de que aceptes.",
              },
            ].map((s) => (
              <article
                key={s.step}
                className="rounded-lg border border-border bg-background p-6 shadow-soft"
              >
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-lg bg-accent/15 text-accent-foreground">
                    <s.icon className="h-5 w-5 text-primary" aria-hidden="true" />
                  </div>
                  <span className="font-display text-sm font-bold uppercase tracking-wider text-primary">
                    Paso {s.step}
                  </span>
                </div>
                <h3 className="mt-4 font-display text-lg font-bold text-secondary">{s.title}</h3>
                <p className="mt-2 text-sm text-secondary/70">{s.desc}</p>
              </article>
            ))}
          </div>
          <p className="mt-8 text-sm text-secondary/60">
            ¿Eres una empresa y buscas contratar anfitrionas, promotoras o impulsadoras para tu
            evento? Visita nuestro{" "}
            <Link
              to="/impulsadoras-promotoras"
              className="font-semibold text-primary hover:underline"
            >
              servicio de anfitrionas, impulsadoras y promotoras
            </Link>
            .
          </p>
        </div>
      </section>

      {/* TODO EL PERÚ — alcance nacional de la convocatoria */}
      <section className="bg-surface py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              · Todo el Perú
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-secondary md:text-4xl">
              Postula desde cualquier ciudad del Perú
            </h2>
            <p className="mt-4 text-secondary/75">
              Las marcas con las que trabajamos hacen campañas en Lima y en regiones: lanzamientos,
              degustaciones en supermercados, ferias y activaciones en centros comerciales. Al
              postular indicas tu ciudad y tu distrito, y te convocamos para las campañas cerca de
              donde vives.
            </p>
          </div>
          <ul
            className="mt-8 flex flex-wrap gap-2"
            aria-label="Ciudades desde las que puedes postular"
          >
            {CIUDADES.map((c) => (
              <li
                key={c}
                className="flex items-center gap-1.5 rounded-full border border-border bg-background px-3.5 py-1.5 text-sm text-secondary/80"
              >
                <MapPin className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                {c}
              </li>
            ))}
            <li className="rounded-full px-3.5 py-1.5 text-sm text-secondary/60">
              y cualquier otra ciudad
            </li>
          </ul>
          <p className="mt-8 text-sm text-secondary/60">
            ¿Quieres saber qué hace una promotora y cuánto gana?{" "}
            <Link
              to="/blog/$slug"
              params={{ slug: "promotoras-de-ventas-funciones-sueldos-y-como-postular" }}
              className="font-semibold text-primary hover:underline"
            >
              Lee la guía de funciones y sueldos de una promotora de ventas
            </Link>
            .
          </p>
        </div>
      </section>

      {/* FAQ */}
      <FAQAccordion items={faqs} title="Preguntas frecuentes sobre el trabajo" />
    </PageShell>
  );
}
